import * as React10 from 'react';
import { Fragment as Fragment$1 } from 'react';
import { intersectsSelectedSet, inSelectedSet, TableFilterBar, rankedCounts, ChainAvatar, Badge, FinalityPill, RiskBadge, cn, columnIds, LiveModeControl, ResourceTable, mergeStreamedRows, useNow, useQuery, notifyError, buildScopeChoices as buildScopeChoices$1, ScopeSwitcher, Spinner, ErrorState, TagList, MiddleEllipsis, CopyButton, SagaPill, TokenValue, AddressDisplay, HashDisplay, AttributionChips } from '@authe/ui-core';
export { readInitialColumnVisibility, readInitialColumnWidths, usePersistColumnVisibility, usePersistColumnWidths, withPinnedColumnsVisible } from '@authe/ui-core';
import { useTableViews, useTableSavedViews } from '@authe/saved-views';
export { columnPrefsToVisibility, sortRulesToSorting, sortingToSortRules, visibilityToColumnPrefs } from '@authe/saved-views';
import { FinalityState, chainDisplayName, getChainMetadata, coerceFinality, resolveFinality, confirmationProgress } from '@authe/fe-commons/chains';
export { coerceFinality } from '@authe/fe-commons/chains';
import { sagaIdLabel, titleRole, sagaLabel } from '@authe/fe-commons/grouping';
import { catalogBundleChips } from '@authe/fe-commons/overlays';
import { formatRelativeTime, formatTimestamp } from '@authe/fe-commons/time';
import { fieldLabels, TX_FIELDS, fieldLabel, getHashTruncation, getAddressTruncation, asString } from '@authe/fe-commons/display';
import { isGlobalTenantSegment } from '@authe/fe-commons/tenancy';
import { riskLevelFromScore } from '@authe/fe-commons/precedence';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { formatUsd } from '@authe/fe-commons/value';
import { DEFAULT_TX_COLUMNS, ExportMenu } from '@authe/export';
export { DEFAULT_TX_COLUMNS, exportTransactionsCsv } from '@authe/export';
import { deriveDustSenders, withoutDust, dustReason } from '@authe/fe-commons/dust';
export { DEFAULT_DUST_USD } from '@authe/fe-commons/dust';
import { deriveImpersonations, poisoningReason } from '@authe/fe-commons/poisoning';
import { useResourceStream } from '@authe/ui-core/stream';
import { useChainTips, useOptionalStreamingClient } from '@authe/streaming';
import { useDepsChanged, useApiClient, useTransport, useGraphqlQuery } from '@authe/ui-core/api';
import { createLogger } from '@authe/fe-commons/logging';
import { scopeReadQuery, TransactionDetailSchema, transactionScopeDescription, transactionScopeLabel } from '@authe/fe-commons/models';
import { readDebugResources, unwrapData } from '@authe/fe-commons/api';
import { useDebugResource } from '@authe/ui-core/debug';
import { parseProcessingStats, parseDecodeConfig, parseGroupView, cdnUrlForKey, isOffloadPlaceholder, rehydrateItem, createEnvelopeBodyLoader, cloneBody, mergeSlice } from '@authe/fe-commons/storage';
import { toNetFlowRows, toTokenFlowRows, coerceTransactionRows, coerceTransactionRow } from '@authe/fe-commons/transpose';
export { coerceTransactionRow, coerceTransactionRows, toNetFlowRows, toTokenFlowRows, txEntryUniqueAddresses, unwrapTxEnvelope } from '@authe/fe-commons/transpose';
import { fetchLatestTransactions } from '@authe/fe-commons/graphql';

// src/TransactionTable.tsx
function isSystemTxRow(row) {
  return isGlobalTenantSegment(row.org_id) && isGlobalTenantSegment(row.tenant_id);
}
function rowProtocols(row) {
  const primary = typeof row.primary_protocol_id === "string" ? row.primary_protocol_id : void 0;
  const rest = Array.isArray(row.protocol_ids) ? row.protocol_ids.filter((id) => Boolean(id)) : [];
  return [...new Set(primary ? [primary, ...rest] : rest)];
}
function otherParty(e) {
  if (!e) return void 0;
  if (e.direction === "in") return e.sender ?? e.counterparty;
  return e.counterparty;
}
function getRowEnrichment(row) {
  const candidate = row.enrichment;
  return candidate && typeof candidate === "object" ? candidate : void 0;
}
function resolveRisk(e) {
  const c = otherParty(e);
  if (!c) return null;
  if (c.riskLevel) return c.riskLevel;
  if (typeof c.riskScore === "number") return riskLevelFromScore(c.riskScore) ?? null;
  return null;
}
function normalizeHashSearch(value) {
  return String(value ?? "").trim().toLowerCase().replace(/^0x/, "");
}
var hashContains = (row, columnId, filterValue) => {
  const raw = String(filterValue ?? "").trim().toLowerCase();
  if (!raw) return true;
  const hay = normalizeHashSearch(row.getValue(columnId));
  const parts = raw.split(/…|\.\.\./);
  if (parts.length === 2) {
    const head = parts[0].replace(/^0x/, "");
    const tail = parts[1];
    return hay.startsWith(head) && (tail === "" || hay.endsWith(tail));
  }
  return hay.includes(normalizeHashSearch(raw));
};
hashContains.autoRemove = (value) => !normalizeHashSearch(value);
function idMatches(id, needle) {
  const hay = id.toLowerCase();
  const parts = needle.split(/…|\.\.\./);
  if (parts.length === 2) {
    const [head, tail] = parts;
    return hay.startsWith(head) && (tail === "" || hay.endsWith(tail));
  }
  return hay.includes(needle);
}
var idsContain = (row, columnId, filterValue) => {
  const needle = String(filterValue ?? "").trim().toLowerCase();
  if (!needle) return true;
  const raw = row.getValue(columnId);
  const ids = Array.isArray(raw) ? raw : [raw];
  return ids.some((id) => idMatches(String(id ?? ""), needle));
};
idsContain.autoRemove = (value) => !String(value ?? "").trim();
var valueUsdInRange = (row, _columnId, filterValue) => {
  const range = filterValue;
  if (!range || range.min == null && range.max == null) return true;
  const usd = getRowEnrichment(row.original)?.valueUsd;
  if (typeof usd !== "number" || !Number.isFinite(usd)) return false;
  if (range.min != null && usd < range.min) return false;
  if (range.max != null && usd > range.max) return false;
  return true;
};
valueUsdInRange.autoRemove = (value) => {
  const range = value;
  return range == null || range.min == null && range.max == null;
};
var sortByBigIntString = (a, b, columnId) => {
  const av = safeBigInt(a.getValue(columnId));
  const bv = safeBigInt(b.getValue(columnId));
  return av < bv ? -1 : av > bv ? 1 : 0;
};
function safeBigInt(value) {
  try {
    return BigInt(value || "0");
  } catch {
    return 0n;
  }
}
function monoColumnWidth(head, tail, affordancePx = 72) {
  return Math.round((head + tail + 1) * 8.5) + affordancePx;
}
var EmDash = () => /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "\u2014" });
function counterpartyBundle(e) {
  return catalogBundleChips(e?.counterparty ?? {});
}
function senderBundle(e) {
  return catalogBundleChips(e?.sender ?? {});
}
function bundleIsEmpty(bundle) {
  return ["entities", "labels", "tags", "groups"].every(
    (k) => (bundle[k] ?? []).length === 0
  );
}
function createTransactionColumns(opts = {}) {
  const {
    explorerLinks = true,
    logoResolver,
    locale,
    onDrilldown,
    columns,
    chainHeads,
    getSaga,
    renderProtocol,
    rowMarkers,
    chipPrefs,
    counterpartyChips = false
  } = opts;
  const getEnrichment = opts.getEnrichment ?? getRowEnrichment;
  const hasEnrichmentReader = Boolean(opts.getEnrichment);
  const hashTrunc = getHashTruncation();
  const addrTrunc = getAddressTruncation();
  const searchFilter = !getSaga ? hashContains : (row, columnId, filterValue, addMeta) => {
    if (hashContains(row, columnId, filterValue)) return true;
    const needle = String(filterValue ?? "").trim().toLowerCase();
    return (getSaga(row.original)?.ids ?? []).some((id) => idMatches(id.value, needle));
  };
  searchFilter.autoRemove = hashContains.autoRemove;
  const chipNames = (bundle) => [
    ...bundle.labels ?? [],
    ...bundle.tags ?? [],
    ...bundle.groups ?? [],
    ...bundle.entities ?? []
  ].map((item) => item.value ? `${item.name}: ${item.value}` : item.name || item.id).filter(Boolean);
  const chipColumn = (id, kind, read, max) => ({
    id,
    header: fieldLabel(id),
    enableSorting: false,
    // The NAMES behind the chips, so the column has a value to filter on.
    //
    // These cells read the enrichment overlay rather than a row field, which
    // until now meant `row.getValue()` returned undefined and a filter over them
    // was impossible — the four most interesting columns in a triage pass (who
    // is this, what did we tag it) were the four you could not narrow by. One
    // entry per chip, so a row carrying three labels is counted under all three
    // and `intersectsSelectedSet` keeps it for any of them.
    accessorFn: (row) => chipNames(read(getEnrichment(row))),
    getUniqueValues: (row) => chipNames(read(getEnrichment(row))),
    filterFn: intersectsSelectedSet,
    meta: { filter: { kind: "facet" } },
    cell: ({ row }) => /* @__PURE__ */ jsx(
      AttributionChips,
      {
        bundle: read(getEnrichment(row.original)),
        kinds: [kind],
        max,
        popoverTitle: fieldLabel(id),
        userPrefs: chipPrefs,
        empty: /* @__PURE__ */ jsx(EmDash, {})
      }
    )
  });
  const AddressChips = ({
    bundle,
    subject
  }) => {
    if (!counterpartyChips) return null;
    if (bundleIsEmpty(bundle)) return null;
    return /* @__PURE__ */ jsx(AttributionChips, { bundle, popoverTitle: subject, userPrefs: chipPrefs });
  };
  const labelColumn = (id, read) => chipColumn(id, "label", (e) => catalogBundleChips({ labels: read(e) }));
  const tagColumn = (id, read) => chipColumn(id, "tag", (e) => catalogBundleChips({ tags: read(e) }));
  const groupColumn = (id, read) => chipColumn(id, "group", (e) => catalogBundleChips({ groups: read(e) }), 2);
  const entityColumn = (id, read) => chipColumn(id, "entity", (e) => catalogBundleChips({ entity: read(e) }));
  const registry = {
    // A placeholder so `select` stays a legal `TxColumnId` for hosts that still
    // name it in a stored column list. Filtered out of the resolved set below:
    // the real checkbox column comes from `ResourceTable` under
    // `enableSelection`, and rendering this stub would draw an empty column.
    select: { id: "select" },
    chain_id: {
      accessorKey: "chain_id",
      header: fieldLabel(TX_FIELDS.chainId),
      filterFn: inSelectedSet,
      // Values are CAIP-2 ids, so the facet has to name them the way the rest of
      // the product does or the reader is picking between `eip155:1` and
      // `eip155:10`.
      meta: { filter: { kind: "facet", label: chainDisplayName } },
      cell: ({ getValue }) => /* @__PURE__ */ jsx(ChainAvatar, { chain: String(getValue() ?? ""), size: "sm", logoResolver })
    },
    tx_hash: {
      accessorKey: "tx_hash",
      header: fieldLabel(TX_FIELDS.txHash),
      enableSorting: false,
      // The only pinned column. Every other one is a matter of what this table is
      // for, but the hash is the row's identity: hide it and a row can't be
      // copied, quoted in a report, drilled into, or told apart from the next one
      // — the table stops being about transactions. Everything else stays the
      // user's call.
      enableHiding: false,
      // Tolerant substring match against the FULL hash accessor — the cell is
      // rendered middle-truncated (cosmetic) but search hits the complete value,
      // normalising whitespace/case/`0x` so a paste of the whole hash (or any
      // fragment of it) matches. Widened to the row's cross-chain ids where the
      // host has them; see `searchFilter`.
      filterFn: searchFilter,
      size: monoColumnWidth(hashTrunc.head, hashTrunc.tail),
      cell: ({ row }) => {
        const hash = row.original.tx_hash;
        if (!hash) return /* @__PURE__ */ jsx(EmDash, {});
        const display = /* @__PURE__ */ jsx(
          HashDisplay,
          {
            hash,
            chain: row.original.chain_id,
            truncate: hashTrunc,
            canonicalTxId: row.original.canonical_tx_id ?? void 0,
            showExplorerIcon: explorerLinks && !onDrilldown,
            onDrilldown
          }
        );
        const system = isSystemTxRow(row.original);
        const marks = rowMarkers?.(row.original);
        if (!system && !marks) return display;
        return /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1.5", children: [
          display,
          system ? /* @__PURE__ */ jsx(
            Badge,
            {
              variant: "outline",
              className: "shrink-0 px-1 py-0 text-[10px] font-normal text-muted-foreground",
              title: "Processed by an always-on system pipeline, visible to every tenant",
              children: "System"
            }
          ) : null,
          marks
        ] });
      }
    },
    block_timestamp: {
      accessorKey: "block_timestamp",
      header: fieldLabel(TX_FIELDS.blockTimestamp),
      // The one column where a range is the natural question — "what happened
      // that week" — and the only one whose sort order the reader can trust
      // without knowing how the feed was fetched, since it is what the feed is
      // already ordered by.
      meta: { filter: { kind: "date" } },
      cell: ({ getValue }) => {
        const ts = Number(getValue() ?? 0);
        if (!ts) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsx("span", { title: formatTimestamp(ts, { locale }), className: "whitespace-nowrap text-muted-foreground", children: formatRelativeTime(ts, { locale }) });
      }
    },
    from: {
      id: "from",
      header: fieldLabel(TX_FIELDS.from),
      enableSorting: false,
      // The full address, so "show me everything touching this wallet" is a
      // filter rather than a re-run of the case. The cell is middle-truncated
      // for width; `hashContains` matches the whole value and tolerates a
      // pasted `0x1234…cdef`, which is what the reader can actually see to copy.
      accessorFn: (row) => getEnrichment(row)?.from ?? "",
      filterFn: hashContains,
      meta: { filter: { kind: "text", placeholder: "0x\u2026" } },
      size: monoColumnWidth(addrTrunc.head, addrTrunc.tail),
      cell: ({ row }) => {
        const e = getEnrichment(row.original);
        const addr = e?.from;
        if (!addr) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsxs("span", { className: "flex flex-col items-start gap-1", children: [
          /* @__PURE__ */ jsx(
            AddressDisplay,
            {
              address: addr,
              chain: row.original.chain_id,
              entityLabel: e?.sender?.entity?.name,
              onDrilldown,
              truncate: addrTrunc
            }
          ),
          /* @__PURE__ */ jsx(AddressChips, { bundle: senderBundle(e), subject: "Sender" })
        ] });
      }
    },
    to: {
      id: "to",
      header: fieldLabel(TX_FIELDS.to),
      enableSorting: false,
      accessorFn: (row) => getEnrichment(row)?.to ?? "",
      filterFn: hashContains,
      meta: { filter: { kind: "text", placeholder: "0x\u2026" } },
      size: monoColumnWidth(addrTrunc.head, addrTrunc.tail),
      cell: ({ row }) => {
        const e = getEnrichment(row.original);
        const addr = e?.to;
        if (!addr) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsxs("span", { className: "flex flex-col items-start gap-1", children: [
          /* @__PURE__ */ jsx(
            AddressDisplay,
            {
              address: addr,
              chain: row.original.chain_id,
              entityLabel: e?.counterparty?.entity?.name,
              onDrilldown,
              truncate: addrTrunc
            }
          ),
          /* @__PURE__ */ jsx(AddressChips, { bundle: counterpartyBundle(e), subject: "Counterparty" })
        ] });
      }
    },
    value: {
      id: "value",
      accessorFn: (row) => row.total_value_raw ?? "0",
      header: fieldLabel(TX_FIELDS.totalValueRaw),
      sortingFn: sortByBigIntString,
      // Sorts on base units; FILTERS on dollars. Those are different quantities
      // and the split is deliberate: "at least 1000000000000000000" is not a
      // question anybody asks, and base units are not comparable across tokens
      // anyway — a row's 1 USDC and another's 1 wei are the same integer. The
      // comparable number is the priced USD value, which lives on the enrichment
      // overlay, so the filter reads it there (`valueUsdInRange`) and `bounds`
      // is off: the column's own min/max are wei and would describe the wrong
      // quantity in a dollar box.
      filterFn: valueUsdInRange,
      meta: {
        align: "right",
        filter: { kind: "number", title: "Value (USD)", bounds: false }
      },
      cell: ({ row }) => {
        const e = getEnrichment(row.original);
        return /* @__PURE__ */ jsx(
          TokenValue,
          {
            amount: row.original.total_value_raw ?? "0",
            chain: row.original.chain_id,
            valueUsd: e?.valueUsd ?? null,
            direction: e?.direction ?? null
          }
        );
      }
    },
    finality: {
      id: "finality",
      // Read straight off the row, so the pill, the sort order and the faceted
      // counts are one answer rather than three. Promoting the persisted state
      // with the live head is `useLiveFinalityRows`' job, on the DATA — an
      // accessor that did it here would be computed once per row and cached, and
      // the facet panel would go on offering "Observed" for rows the cells had
      // already promoted (see that hook's note on TanStack's value cache).
      accessorFn: (row) => coerceFinality(row.finality) ?? FinalityState.UNKNOWN,
      header: fieldLabel(TX_FIELDS.finality),
      filterFn: inSelectedSet,
      meta: { filter: { kind: "facet" } },
      cell: ({ row, getValue }) => {
        const state = getValue();
        const chainId = String(row.original.chain_id ?? "").toLowerCase();
        const confirmations = confirmationProgress(state, {
          blockNumber: row.original.block_number ?? null,
          head: chainHeads?.[chainId] ?? null,
          chainOrId: chainId
        });
        return /* @__PURE__ */ jsx(FinalityPill, { state, confirmations: confirmations ?? void 0 });
      }
    },
    type: {
      accessorKey: "type",
      header: fieldLabel(TX_FIELDS.type),
      filterFn: inSelectedSet,
      meta: { filter: { kind: "facet" } },
      cell: ({ row, getValue }) => {
        const isContract = getEnrichment(row.original)?.contractInteraction;
        const label = String(getValue() ?? "transaction");
        return isContract ? /* @__PURE__ */ jsx(
          Badge,
          {
            variant: "outline",
            className: "border-risk-medium/40 bg-risk-medium/10 font-normal capitalize text-risk-medium",
            title: "Contract interaction",
            children: label
          }
        ) : /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "font-normal capitalize", children: label });
      }
    },
    // --- the transaction's own attribution -----------------------------------
    // What the table's bulk bar and the inspector write via `PATCH
    // /transactions/{id}`. Tenant-wide and chain-agnostic: a transaction's
    // overlays carry no per-user visibility, so these chips have no scope dot.
    tx_labels: labelColumn("tx_labels", (e) => e?.labels),
    tx_tags: tagColumn("tx_tags", (e) => e?.tags),
    tx_groups: groupColumn("tx_groups", (e) => e?.groups),
    tx_entity: entityColumn("tx_entity", (e) => e?.entity),
    // --- the counterparty address's attribution ------------------------------
    // A DIFFERENT claim about a DIFFERENT thing: written per `(chain, address)`
    // through the assignments API, by someone saying "this wallet is Binance",
    // not "this transfer is suspicious". Describes the row's `to` end (or `from`
    // on a one-ended row) — see the host's enrichment reader for which.
    //
    // Through `otherParty`: on a feed with a subject, "counterparty" is the end
    // that is NOT the subject, which on an incoming row is the sender. The `to`
    // cell still draws `counterparty` under its own address; these columns ask
    // who is on the other side, and must not answer "the wallet you are
    // investigating".
    counterparty_labels: labelColumn("counterparty_labels", (e) => otherParty(e)?.labels),
    counterparty_tags: tagColumn("counterparty_tags", (e) => otherParty(e)?.tags),
    counterparty_groups: groupColumn("counterparty_groups", (e) => otherParty(e)?.groups),
    counterparty_entity: entityColumn("counterparty_entity", (e) => otherParty(e)?.entity),
    // --- the sending address's attribution -----------------------------------
    // The same claim about the row's other end. Off by default like the
    // counterparty set and for the same reason: the chips are already under the
    // address in the `from` cell, and a column repeats them further right under
    // a header rather than under their subject. They exist for the case that
    // wants to FILTER or scan one kind on its own — which is the whole point,
    // since a facet needs a column to hang off.
    sender_labels: labelColumn("sender_labels", (e) => e?.sender?.labels),
    sender_tags: tagColumn("sender_tags", (e) => e?.sender?.tags),
    sender_groups: groupColumn("sender_groups", (e) => e?.sender?.groups),
    sender_entity: entityColumn("sender_entity", (e) => e?.sender?.entity),
    counterparty_risk: {
      id: "counterparty_risk",
      header: fieldLabel("counterparty_risk"),
      enableSorting: false,
      // "Show me only the risky counterparties" is the whole reason this column
      // is ever turned on, and until it had a value it could be read but not
      // narrowed by. Rows with no resolved risk carry `""`, which the facet
      // drops rather than offering as a nameless option.
      accessorFn: (row) => resolveRisk(getEnrichment(row)) ?? "",
      filterFn: inSelectedSet,
      meta: { filter: { kind: "facet" } },
      cell: ({ row }) => {
        const e = getEnrichment(row.original);
        const level = resolveRisk(e);
        return level ? /* @__PURE__ */ jsx(RiskBadge, { level, score: otherParty(e)?.riskScore }) : /* @__PURE__ */ jsx(EmDash, {});
      }
    },
    /**
     * What the row IS — poisoning, dust — with the reason one hover away.
     *
     * The answer to "which of these transactions are spam", which this table had
     * no column for: the address columns say what is known about a WALLET, and a
     * poisoning transfer's sender is often a wallet nobody has tagged. The flag
     * is the row's own classification, computed by the host over the history it
     * holds (`classifyTxRows`), so it is present exactly where the evidence is.
     *
     * Faceted like the risk column, because "show me only the poisoning" is the
     * question the column exists for.
     */
    row_flags: {
      id: "row_flags",
      header: fieldLabel("row_flags"),
      enableSorting: false,
      accessorFn: (row) => getEnrichment(row)?.flags?.[0]?.kind ?? "",
      filterFn: inSelectedSet,
      meta: { filter: { kind: "facet" } },
      cell: ({ row }) => {
        const flags = getEnrichment(row.original)?.flags ?? [];
        if (flags.length === 0) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsx("span", { className: "flex flex-wrap gap-1", children: flags.map((flag) => /* @__PURE__ */ jsx(
          Badge,
          {
            variant: "outline",
            title: flag.reason,
            className: flag.kind === "poisoning" ? "border-risk-high/40 bg-risk-high/10 font-normal text-risk-high" : "border-muted-foreground/30 bg-muted font-normal text-muted-foreground",
            children: flag.label
          },
          flag.kind
        )) });
      }
    },
    /**
     * The caller's own analyst note.
     *
     * The one column here that carries no chain fact — everything else is
     * something the network said, and this is something a person did. It has
     * been writable for a long time and readable only by opening the
     * transaction, which made it a place notes went to be forgotten: the whole
     * value of "I checked this counterparty myself" is that the next person
     * scanning the table sees it without opening anything.
     *
     * Truncated to one line with the full text on hover, because a note is prose
     * of arbitrary length in a row of fixed height, and a column that grows with
     * its longest note makes every other row unreadable.
     *
     * Off by default, like the other opt-in columns: most feeds have no notes,
     * and a permanently empty column is a permanent cost.
     */
    notes: {
      id: "notes",
      // The header carries the scope, because the column cannot. A note here is
      // about THIS transaction; a fact about the counterparty belongs on the
      // address, where one edit covers every row that touches it instead of
      // forty rows disagreeing with each other.
      header: () => /* @__PURE__ */ jsx("span", { title: "Your note on this transaction. A fact about an address belongs on the address.", children: fieldLabel("notes") }),
      enableSorting: false,
      accessorFn: (row) => {
        const note = row.notes;
        return typeof note === "string" ? note : "";
      },
      meta: { filter: { kind: "text", placeholder: "Search notes\u2026" } },
      cell: ({ row }) => {
        const note = row.original.notes;
        const text = typeof note === "string" ? note.trim() : "";
        if (!text) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsx("span", { className: "block max-w-[22rem] truncate text-xs", title: text, children: text });
      }
    },
    // Distinct from `groups` above, which shows the CURATED catalog collections a
    // row was filed into. This is the machine cross-chain saga: the legs a
    // protocol's own correlation id ties together, and whether they all arrived.
    saga: {
      id: "saga",
      header: fieldLabel("saga"),
      enableSorting: false,
      // The one question a saga column exists to answer — "did it land?" — as a
      // value the toolbar can narrow by. A row that is not a bridge leg has no
      // saga at all and carries `""`, which the facet drops: "not part of a
      // saga" is the absence of the subject, not a third outcome alongside
      // complete and incomplete.
      accessorFn: (row) => {
        const saga = getSaga?.(row);
        if (!saga || saga.total === 0) return "";
        return saga.complete ? "complete" : "incomplete";
      },
      filterFn: inSelectedSet,
      meta: { filter: { kind: "facet" } },
      cell: ({ row }) => {
        const saga = getSaga?.(row.original);
        if (!saga || saga.total === 0) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5", children: [
          /* @__PURE__ */ jsx(
            SagaPill,
            {
              label: sagaLabel(saga.sagaKind),
              missing: saga.missingRoles.map(titleRole),
              complete: saga.complete,
              total: saga.total,
              confirmed: saga.confirmed ?? false
            }
          ),
          saga.role ? /* @__PURE__ */ jsx("span", { className: "text-[10px] capitalize text-muted-foreground", children: saga.role }) : null
        ] });
      }
    },
    // The correlation id itself, where `saga` above renders whether every leg
    // arrived. This is the value that goes into a report, or into the protocol's
    // own explorer, and it's what makes two rows on different chains provably the
    // same message. Both columns read the same derived sagas, so a host already
    // showing one pays nothing to show the other.
    cross_chain_id: {
      id: "cross_chain_id",
      header: fieldLabel("cross_chain_id"),
      enableSorting: false,
      // The COMPLETE ids: the cell is middle-truncated for width, but a pasted
      // message id has to find its row.
      accessorFn: (row) => (getSaga?.(row)?.ids ?? []).map((id) => id.value),
      filterFn: idsContain,
      meta: { filter: { kind: "text", placeholder: "Paste a message id\u2026" } },
      size: monoColumnWidth(hashTrunc.head, hashTrunc.tail),
      cell: ({ row }) => {
        const saga = getSaga?.(row.original);
        const ids = saga?.ids ?? [];
        if (ids.length === 0) {
          return saga?.pending ? /* @__PURE__ */ jsx("span", { className: "text-xs italic text-muted-foreground", title: "This row's decoded body hasn't been loaded, so its cross-chain ids are unknown.", children: "Not decoded" }) : /* @__PURE__ */ jsx(EmDash, {});
        }
        return /* @__PURE__ */ jsx(
          TagList,
          {
            popoverTitle: "Cross-chain ids",
            max: 1,
            items: ids.map((id) => /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1", children: [
              /* @__PURE__ */ jsx(
                MiddleEllipsis,
                {
                  value: id.value,
                  head: hashTrunc.head,
                  tail: hashTrunc.tail,
                  title: `${sagaIdLabel(id.label)}: ${id.value}`,
                  className: "text-xs"
                }
              ),
              /* @__PURE__ */ jsx(CopyButton, { value: id.value, label: `Copy ${sagaIdLabel(id.label)}` })
            ] }, id.value))
          }
        );
      }
    },
    primary_entity_id: {
      accessorKey: "primary_entity_id",
      header: fieldLabel(TX_FIELDS.primaryEntityId),
      meta: { filter: { kind: "text" } },
      cell: ({ getValue }) => {
        const id = getValue();
        return id ? /* @__PURE__ */ jsx("span", { className: "text-sm", children: id }) : /* @__PURE__ */ jsx(EmDash, {});
      }
    },
    protocol: {
      id: "protocol",
      // Faceting counts each protocol separately rather than treating a row's
      // whole set as one value, so the toolbar filter can offer "chainlink_ccip
      // (12)" over rows that also carry three other protocols.
      accessorFn: (row) => rowProtocols(row),
      getUniqueValues: (row) => rowProtocols(row),
      header: fieldLabel("protocol"),
      enableSorting: false,
      filterFn: intersectsSelectedSet,
      // The facet's VALUES come from the page (see `getUniqueValues` above); its
      // display names do not — only the brand registry turns `chainlink_ccip`
      // into "Chainlink CCIP", and that lives in the host. It arrives as a
      // filter override.
      meta: { filter: { kind: "facet" } },
      cell: ({ row }) => {
        const protocols = rowProtocols(row.original);
        if (protocols.length === 0) return /* @__PURE__ */ jsx(EmDash, {});
        return /* @__PURE__ */ jsx(
          TagList,
          {
            popoverTitle: "Protocols",
            max: 2,
            items: protocols.map((id) => (
              // The neutral fallback shows the raw id rather than a humanized
              // one: `erc20` / `chainlink_ccip` are what the API returns and what
              // an investigator cross-references, and Title Case would render
              // them as "Erc20". Hosts that want display names inject a renderer
              // backed by the brand registry, which actually knows them.
              /* @__PURE__ */ jsx(Fragment$1, { children: renderProtocol?.(id, row.original) ?? /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "font-normal", children: id }) }, id)
            ))
          }
        );
      }
    }
  };
  const defaultColumns = hasEnrichmentReader ? [
    "chain_id",
    "tx_hash",
    "from",
    "to",
    "value",
    "counterparty_risk",
    "counterparty_labels",
    "counterparty_tags",
    "type",
    "finality"
  ] : ["chain_id", "tx_hash", "block_timestamp", "value", "finality", "type", "primary_entity_id"];
  const selected = columns ?? defaultColumns;
  return selected.filter((id) => id !== "select" && registry[id]).map((id) => registry[id]);
}
var TX_COLUMN_LABELS = fieldLabels(
  [
    TX_FIELDS.chainId,
    TX_FIELDS.txHash,
    TX_FIELDS.blockTimestamp,
    TX_FIELDS.from,
    TX_FIELDS.to,
    TX_FIELDS.finality,
    TX_FIELDS.type,
    TX_FIELDS.primaryEntityId
  ],
  {
    value: fieldLabel(TX_FIELDS.totalValueRaw),
    saga: fieldLabel("saga"),
    cross_chain_id: fieldLabel("cross_chain_id"),
    protocol: fieldLabel("protocol"),
    // Both attribution subjects, spelled out: this map IS the column menu, and
    // the menu is the only place a user learns that the table can tell them apart.
    tx_labels: fieldLabel("tx_labels"),
    tx_tags: fieldLabel("tx_tags"),
    tx_groups: fieldLabel("tx_groups"),
    tx_entity: fieldLabel("tx_entity"),
    counterparty_labels: fieldLabel("counterparty_labels"),
    counterparty_tags: fieldLabel("counterparty_tags"),
    counterparty_groups: fieldLabel("counterparty_groups"),
    counterparty_entity: fieldLabel("counterparty_entity"),
    counterparty_risk: fieldLabel("counterparty_risk"),
    row_flags: fieldLabel("row_flags"),
    sender_labels: fieldLabel("sender_labels"),
    sender_tags: fieldLabel("sender_tags"),
    sender_groups: fieldLabel("sender_groups"),
    sender_entity: fieldLabel("sender_entity"),
    // In the menu but not in any default set: most feeds have no notes, and a
    // permanently empty column is a permanent cost. The menu IS how somebody
    // learns the table can show one.
    notes: fieldLabel("notes")
  }
);
var DEFAULT_TX_FACETS = ["chain_id", "finality", "type"];
function transactionFilterOverrides(options, labels) {
  const overrides = {};
  for (const [field, value] of Object.entries(options ?? {})) {
    if (value) overrides[field] = { ...overrides[field], options: value };
  }
  for (const [field, value] of Object.entries(labels ?? {})) {
    if (value) overrides[field] = { ...overrides[field], label: value };
  }
  return overrides;
}
function TransactionFacets({
  table,
  facets = DEFAULT_TX_FACETS,
  options,
  labels
}) {
  return /* @__PURE__ */ jsx(
    TableFilterBar,
    {
      table,
      columns: facets,
      overrides: transactionFilterOverrides(options, labels)
    }
  );
}
function summarizeTransactions(rows, getEnrichment = getRowEnrichment) {
  const list = Array.isArray(rows) ? rows : [];
  const chains = /* @__PURE__ */ new Map();
  const finality = /* @__PURE__ */ new Map();
  const types = /* @__PURE__ */ new Map();
  const risk = /* @__PURE__ */ new Map();
  let totalValueUsd = null;
  let contractInteractions = 0;
  const bump = (map, key) => {
    if (key == null || key === "") return;
    map.set(key, (map.get(key) ?? 0) + 1);
  };
  for (const row of list) {
    bump(chains, row.chain_id);
    bump(finality, row.finality ? String(row.finality) : null);
    bump(types, row.type ? String(row.type) : null);
    const e = getEnrichment(row);
    const level = resolveRisk(e);
    if (level) risk.set(level, (risk.get(level) ?? 0) + 1);
    if (e?.valueUsd != null) totalValueUsd = (totalValueUsd ?? 0) + e.valueUsd;
    if (e?.contractInteraction) contractInteractions += 1;
  }
  return {
    total: list.length,
    chains: rankedCounts(chains),
    finality: rankedCounts(finality),
    types: rankedCounts(types),
    risk: [...risk.entries()].map(([level, count]) => ({ level, count })),
    totalValueUsd,
    contractInteractions
  };
}
function Stat({ label, children }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5", children: [
    /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: label }),
    children
  ] });
}
function TransactionLegend({
  rows,
  getEnrichment,
  maxChains = 6,
  className
}) {
  const summary = React10.useMemo(() => summarizeTransactions(rows, getEnrichment), [rows, getEnrichment]);
  const shownChains = summary.chains.slice(0, maxChains);
  const extraChains = summary.chains.length - shownChains.length;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "scroll-fade-x flex flex-wrap items-center gap-x-5 gap-y-2 rounded-md border bg-card/40 px-3 py-2 text-sm",
        className
      ),
      children: [
        /* @__PURE__ */ jsx(Stat, { label: "Transactions", children: /* @__PURE__ */ jsx("span", { className: "font-semibold tabular-nums", children: summary.total.toLocaleString() }) }),
        summary.totalValueUsd != null && /* @__PURE__ */ jsx(Stat, { label: "Total", children: /* @__PURE__ */ jsx("span", { className: "font-semibold tabular-nums", children: formatUsd(summary.totalValueUsd) }) }),
        shownChains.length > 0 && /* @__PURE__ */ jsx(Stat, { label: fieldLabel("chain_id"), children: /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-1", children: [
          shownChains.map((c) => /* @__PURE__ */ jsxs(
            "span",
            {
              className: "inline-flex items-center gap-1",
              title: getChainMetadata(c.key)?.name ?? c.key,
              children: [
                /* @__PURE__ */ jsx(ChainAvatar, { chain: c.key, size: "sm", showName: false }),
                /* @__PURE__ */ jsx("span", { className: "text-xs tabular-nums text-muted-foreground", children: c.count })
              ]
            },
            c.key
          )),
          extraChains > 0 && /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-muted-foreground", children: [
            "+",
            extraChains
          ] })
        ] }) }),
        summary.finality.length > 0 && /* @__PURE__ */ jsx(Stat, { label: fieldLabel("finality"), children: /* @__PURE__ */ jsx("span", { className: "flex flex-wrap items-center gap-1", children: summary.finality.map((f) => {
          const state = coerceFinality(f.key);
          return state ? /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
            /* @__PURE__ */ jsx(FinalityPill, { state }),
            /* @__PURE__ */ jsx("span", { className: "text-xs tabular-nums text-muted-foreground", children: f.count })
          ] }, f.key) : /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-muted-foreground", children: [
            f.key,
            " ",
            f.count
          ] }, f.key);
        }) }) }),
        summary.risk.length > 0 && /* @__PURE__ */ jsx(Stat, { label: fieldLabel("risk_score"), children: /* @__PURE__ */ jsx("span", { className: "flex flex-wrap items-center gap-1", children: summary.risk.map((r) => /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
          /* @__PURE__ */ jsx(RiskBadge, { level: r.level }),
          /* @__PURE__ */ jsx("span", { className: "text-xs tabular-nums text-muted-foreground", children: r.count })
        ] }, r.level)) }) }),
        summary.contractInteractions > 0 && /* @__PURE__ */ jsx(Stat, { label: "Contracts", children: /* @__PURE__ */ jsx("span", { className: "font-medium tabular-nums", children: summary.contractInteractions }) })
      ]
    }
  );
}
var CANONICAL = DEFAULT_TX_COLUMNS;
function names(refs) {
  return (refs ?? []).map((ref) => ref.name ?? ref.label ?? "").filter(Boolean).join(", ");
}
function overlay(key, header, read) {
  return { key, header, value: (row) => read(getRowEnrichment(row)) ?? "" };
}
var TX_EXPORT_COLUMNS = [
  ...CANONICAL.map((c) => typeof c === "string" ? { key: c, header: fieldLabel(c) } : c),
  overlay(TX_FIELDS.from, fieldLabel(TX_FIELDS.from), (e) => e?.from),
  overlay(TX_FIELDS.to, fieldLabel(TX_FIELDS.to), (e) => e?.to),
  overlay(TX_FIELDS.direction, "Direction", (e) => e?.direction),
  // The PRICED value the cell shows. The row's own `value_usd` is frequently
  // null on a list row, so exporting it alone printed a blank column next to a
  // screen that was showing a number.
  overlay("value_usd_enriched", "Value (USD)", (e) => e?.valueUsd),
  overlay("tx_labels", fieldLabel("tx_labels"), (e) => names(e?.labels)),
  overlay("tx_tags", fieldLabel("tx_tags"), (e) => names(e?.tags)),
  overlay("tx_entity", fieldLabel("tx_entity"), (e) => e?.entity?.name),
  overlay("counterparty_entity", fieldLabel("counterparty_entity"), (e) => e?.counterparty?.entity?.name),
  overlay("counterparty_labels", fieldLabel("counterparty_labels"), (e) => names(e?.counterparty?.labels)),
  overlay("counterparty_tags", fieldLabel("counterparty_tags"), (e) => names(e?.counterparty?.tags)),
  { key: "counterparty_risk", header: fieldLabel("counterparty_risk"), value: (row) => resolveRisk(getRowEnrichment(row)) ?? "" }
];
function TransactionExportMenu({
  rows,
  columns,
  formats,
  filenameBase = "transactions",
  label,
  disabled,
  exportContext,
  onError
}) {
  return /* @__PURE__ */ jsx(
    ExportMenu,
    {
      rows,
      columns: columns ?? TX_EXPORT_COLUMNS,
      formats,
      filenameBase,
      label,
      disabled,
      exportContext: exportContext ?? { resource: "transactions" },
      onError
    }
  );
}

// src/filters.ts
var TX_FILTER_OPS = {
  /** Membership in a set (multi-select facets: chain_id, finality, type). */
  IN: "in",
  /** Exact match. */
  EQ: "eq",
  /** Case-insensitive substring (hash, address). */
  CONTAINS: "contains",
  /** Numeric/temporal range bounds. */
  GTE: "gte",
  LTE: "lte",
  /** Catalog membership filters. */
  HAS_LABEL: "has_label",
  HAS_TAG: "has_tag",
  HAS_GROUP: "has_group",
  HAS_ENTITY: "has_entity"
};
var CLIENT_FACET_FIELDS = /* @__PURE__ */ new Set(["chain_id", "finality", "type"]);
var CLIENT_CONTAINS_FIELDS = /* @__PURE__ */ new Set(["tx_hash"]);
function asStringArray(value) {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (value == null || value === "") return [];
  return [String(value)];
}
function filterRulesToQuery(rules) {
  const query = {};
  const pushAll = (key, values) => {
    if (values.length === 0) return;
    const existing = Array.isArray(query[key]) ? query[key] : [];
    query[key] = [...existing, ...values];
  };
  for (const rule of rules ?? []) {
    switch (rule.op) {
      case TX_FILTER_OPS.IN:
        pushAll(rule.field, asStringArray(rule.value));
        break;
      case TX_FILTER_OPS.EQ:
      case TX_FILTER_OPS.CONTAINS:
        if (rule.value != null && rule.value !== "") query[rule.field] = String(rule.value);
        break;
      case TX_FILTER_OPS.GTE:
        if (rule.value != null) query[`${rule.field}_gte`] = String(rule.value);
        break;
      case TX_FILTER_OPS.LTE:
        if (rule.value != null) query[`${rule.field}_lte`] = String(rule.value);
        break;
      case TX_FILTER_OPS.HAS_LABEL:
        pushAll("label", asStringArray(rule.value));
        break;
      case TX_FILTER_OPS.HAS_TAG:
        pushAll("tag", asStringArray(rule.value));
        break;
      case TX_FILTER_OPS.HAS_GROUP:
        pushAll("group", asStringArray(rule.value));
        break;
      case TX_FILTER_OPS.HAS_ENTITY:
        if (rule.value != null && rule.value !== "") query.entity_id = String(rule.value);
        break;
    }
  }
  return query;
}
function filterRulesToColumnFilters(rules) {
  const filters = [];
  for (const rule of rules ?? []) {
    if (rule.op === TX_FILTER_OPS.IN && CLIENT_FACET_FIELDS.has(rule.field)) {
      const values = asStringArray(rule.value);
      if (values.length) filters.push({ id: rule.field, value: values });
    } else if (rule.op === TX_FILTER_OPS.CONTAINS && CLIENT_CONTAINS_FIELDS.has(rule.field)) {
      if (rule.value != null && rule.value !== "") filters.push({ id: rule.field, value: String(rule.value) });
    }
  }
  return filters;
}
function columnFiltersToFilterRules(filters) {
  const rules = [];
  for (const f of filters) {
    if (Array.isArray(f.value)) {
      const values = f.value.map(String).filter(Boolean);
      if (values.length) rules.push({ field: f.id, op: TX_FILTER_OPS.IN, value: values });
    } else if (typeof f.value === "string" && f.value !== "") {
      rules.push({ field: f.id, op: TX_FILTER_OPS.CONTAINS, value: f.value });
    }
  }
  return rules;
}
var TRANSACTIONS_TABLE_ID = "transactions";
var TX_FILTER_CODEC = {
  toRules: columnFiltersToFilterRules,
  fromRules: filterRulesToColumnFilters
};
function useTransactionViews(options) {
  return useTableViews({
    ...options,
    tableId: TRANSACTIONS_TABLE_ID,
    filterCodec: TX_FILTER_CODEC
  });
}

// src/rowId.ts
function identifying(value) {
  return typeof value === "string" && value !== "" ? value : void 0;
}
function txRowId(row) {
  return identifying(row.canonical_tx_id) ?? identifying(row.tx_hash);
}
function txStreamKey(row) {
  const id = txRowId(row);
  if (id == null) return null;
  const log5 = row.log_index;
  return log5 == null ? id : `${id}:${String(log5)}`;
}
function txSelectionRowId(row, index) {
  return txStreamKey(row) ?? `idx:${index}`;
}
function TransactionTable({
  rows,
  columns,
  isLoading = false,
  isFetching = false,
  error,
  onRetry,
  onRowClick,
  activeRowId,
  enableSelection = false,
  enableToolbar = true,
  searchPlaceholder = "Search by tx hash\u2026",
  facetedFilters,
  facets,
  toolbarActions,
  enableSavedViews = false,
  enableExport = false,
  exportRows,
  exportFilenameBase,
  selectionActions,
  pagination,
  viewKey,
  defaultColumnVisibility,
  columnVisibility,
  onColumnVisibilityChange,
  columnFilters,
  onColumnFiltersChange,
  sorting,
  onSortingChange,
  columnOrder,
  onColumnOrderChange,
  enableColumnReorder = true,
  density,
  rowHeight = 44,
  enableColumnResizing = false,
  rowClassName,
  onEndReached,
  live,
  liveMode,
  onLiveModeChange,
  liveAvailable = true,
  showLegend = false,
  getEnrichment = getRowEnrichment,
  renderRowDetail,
  detailTitle,
  detailEyebrow = "Transaction",
  detailSubtitle,
  detailFooter,
  height = "h-[600px]",
  emptyTitle = "No transactions",
  emptyDescription = "No transactions match the current filters.",
  className
}) {
  const resolvedColumns = React10.useMemo(
    () => columns ?? createTransactionColumns(),
    [columns]
  );
  const hostControlsState = columnVisibility !== void 0 || columnFilters !== void 0 || sorting !== void 0 || columnOrder !== void 0;
  const savedViews = useTableSavedViews({
    enabled: enableSavedViews && !hostControlsState,
    tableId: viewKey ?? TRANSACTIONS_TABLE_ID,
    columnIds: React10.useMemo(() => columnIds(resolvedColumns), [resolvedColumns]),
    filterCodec: TX_FILTER_CODEC
  });
  const renderToolbarActions = React10.useCallback(
    (table) => /* @__PURE__ */ jsxs(Fragment, { children: [
      savedViews.toolbar,
      enableExport && /* @__PURE__ */ jsx(
        TransactionExportMenu,
        {
          rows: exportRows ?? (() => table.getFilteredRowModel().rows.map((row) => row.original)),
          exportContext: { resource: "transactions", ...savedViews.exportContext },
          ...exportFilenameBase ? { filenameBase: exportFilenameBase } : {}
        }
      ),
      toolbarActions
    ] }),
    [
      savedViews.toolbar,
      savedViews.exportContext,
      enableExport,
      exportRows,
      exportFilenameBase,
      toolbarActions
    ]
  );
  const renderFacets = React10.useCallback(
    () => /* @__PURE__ */ jsxs(Fragment, { children: [
      onLiveModeChange && /* @__PURE__ */ jsx(
        LiveModeControl,
        {
          mode: liveMode ?? "off",
          onChange: onLiveModeChange,
          liveAvailable,
          size: "sm"
        }
      ),
      live,
      facetedFilters
    ] }),
    [onLiveModeChange, liveMode, liveAvailable, live, facetedFilters]
  );
  const renderLegend = React10.useCallback(
    (table) => {
      const filtered = table.getFilteredRowModel().rows.map((row) => row.original);
      if (filtered.length === 0) return null;
      return /* @__PURE__ */ jsx(TransactionLegend, { rows: filtered, getEnrichment });
    },
    [getEnrichment]
  );
  return /* @__PURE__ */ jsx(
    ResourceTable,
    {
      rows,
      columns: resolvedColumns,
      getRowId: txRowId,
      isLoading,
      isFetching,
      error,
      onRetry,
      toolbar: enableToolbar ? {
        searchColumn: "tx_hash",
        searchPlaceholder,
        columnLabels: TX_COLUMN_LABELS,
        filterColumns: facets?.facets ?? DEFAULT_TX_FACETS,
        filterOverrides: transactionFilterOverrides(facets?.options, facets?.labels)
      } : false,
      facets: renderFacets,
      toolbarActions: renderToolbarActions,
      banner: showLegend ? renderLegend : void 0,
      selectionActions: enableSelection ? selectionActions : void 0,
      enableRowSelection: enableSelection,
      enableColumnResizing,
      pagination,
      viewKey,
      defaultColumnVisibility,
      columnVisibility: columnVisibility ?? savedViews.controlled.columnVisibility,
      onColumnVisibilityChange: onColumnVisibilityChange ?? savedViews.controlled.onColumnVisibilityChange,
      columnFilters: columnFilters ?? savedViews.controlled.columnFilters,
      onColumnFiltersChange: onColumnFiltersChange ?? savedViews.controlled.onColumnFiltersChange,
      sorting: sorting ?? savedViews.controlled.sorting,
      onSortingChange: onSortingChange ?? savedViews.controlled.onSortingChange,
      columnOrder: columnOrder ?? savedViews.controlled.columnOrder,
      onColumnOrderChange: onColumnOrderChange ?? savedViews.controlled.onColumnOrderChange,
      enableColumnReorder,
      density: density ?? savedViews.density,
      rowHeight,
      height,
      activeRowId,
      rowClassName,
      ...onEndReached ? { onEndReached } : {},
      onRowClick,
      renderRowDetail,
      detailTitle,
      detailEyebrow,
      detailSubtitle,
      detailFooter,
      emptyTitle,
      emptyDescription,
      className
    }
  );
}
function txRowCandidate(row, getEnrichment) {
  const enrichment = getEnrichment?.(row);
  const protocols = rowProtocols(row);
  return {
    canonicalTxId: txRowId(row),
    valueUsd: row.value_usd ?? null,
    primaryProtocolId: row.primary_protocol_id ?? null,
    protocolIds: protocols,
    fromAddress: enrichment?.from ?? null,
    // `enrichment.to`, NOT `counterparty.address`. The counterparty is "the
    // row's `to`, or `from` on a one-ended row" — so on those rows it is the
    // SENDER, and feeding it here would count a duster as its own recipient.
    // The recipient spread is the entire point of the campaign check: it is what
    // separates a duster from a payroll contract paying the same fifty people.
    toAddress: enrichment?.to ?? null
  };
}
function withoutDustRows(rows, options = {}) {
  const read = (row) => txRowCandidate(row, options.getEnrichment);
  const dustSenders = options.dustSenders ?? (options.deriveSenders ? deriveDustSenders(rows, read) : void 0);
  return withoutDust(rows, read, {
    usdFloor: options.usdFloor,
    protectedIds: options.protectedIds,
    dustSenders
  });
}
function classifyTxRows(rows, options = {}) {
  const read = (row) => txRowCandidate(row, options.getEnrichment);
  const impersonations = options.subjects ? deriveImpersonations(rows, read, options.subjects) : /* @__PURE__ */ new Map();
  const dustSenders = options.dustSenders ?? (options.deriveSenders ? deriveDustSenders(rows, read) : void 0);
  const flags = /* @__PURE__ */ new Map();
  for (const row of rows) {
    const id = txRowId(row);
    if (!id) continue;
    const candidate = read(row);
    const poisoning = poisoningReason(candidate, impersonations);
    if (poisoning) {
      flags.set(id, { kind: "poisoning", label: "Address poisoning", reason: poisoning });
      continue;
    }
    const dust = dustReason(candidate, { usdFloor: options.usdFloor, dustSenders });
    if (dust) flags.set(id, { kind: "dust", label: "Dust", reason: dust });
  }
  return { flags, impersonations };
}
function flaggedIds(classification, kind, protectedIds = /* @__PURE__ */ new Set()) {
  const out = /* @__PURE__ */ new Set();
  for (const [id, flag] of classification.flags) {
    if (flag.kind === kind && !protectedIds.has(id)) out.add(id);
  }
  return out;
}
function txRowKey(row) {
  return txStreamKey(row);
}
function mergeTransactionRows(streamed, fetched, max = 1e3) {
  return mergeStreamedRows(streamed, fetched, txRowKey, max);
}
function defaultParse(message) {
  const payload = message.payload;
  if (!payload || typeof payload !== "object") return null;
  if (payload.tx_hash == null && payload["canonical_tx_id"] == null) return null;
  return payload;
}
function useTransactionStream(options) {
  const { channel, enabled = true, max = 500, parse } = options;
  return useResourceStream({
    channel,
    rowKey: txRowKey,
    parse: parse ?? defaultParse,
    enabled,
    max
  });
}
function useChainHeads(options) {
  const tips = useChainTips(options);
  return React10.useMemo(
    () => Object.fromEntries(Object.values(tips).map((tip) => [tip.chainId, tip.headBlock])),
    [tips]
  );
}
function withLiveFinality(rows, heads, now) {
  let moved = false;
  const next = rows.map((row) => {
    const chainId = String(row.chain_id ?? "").toLowerCase();
    const finality = resolveFinality({
      rowFinality: row.finality,
      blockNumber: row.block_number ?? null,
      head: heads[chainId] ?? null,
      blockTimestamp: row.block_timestamp ?? null,
      now,
      chainOrId: chainId
    });
    if (finality === row.finality) return row;
    moved = true;
    return { ...row, finality };
  });
  return moved ? next : rows;
}
function useLiveFinalityRows(rows, heads) {
  const now = useNow();
  return React10.useMemo(() => withLiveFinality(rows, heads, now), [rows, heads, now]);
}
var log = createLogger("tx-table:query");
var EXPORT_ROW_CEILING = 1e5;
function useTransactionsQuery(options = {}) {
  const { resource = "transactions", pageSize = 50, query, enabled = true, notifyOnError = false } = options;
  const serializedQuery = JSON.stringify(query ?? {});
  const [cursorStack, setCursorStack] = React10.useState([]);
  const cursor = cursorStack[cursorStack.length - 1] ?? null;
  if (useDepsChanged([serializedQuery, resource, pageSize])) {
    setCursorStack((stack) => stack.length === 0 ? stack : []);
  }
  const result = useQuery(
    (client2, signal) => client2.get(`/${resource}`, {
      query: { ...query ?? {}, limit: pageSize, cursor: cursor ?? void 0 },
      signal
    }),
    {
      enabled,
      deps: [cursor, serializedQuery, resource, pageSize],
      // Every dep here describes the SAME list — a cursor, a filter, a page
      // size — so holding the current page while the next one loads reads as a
      // page turn rather than the table blinking out. (A detail read keyed by a
      // record id must NOT do this: see `useQuery`'s note.) Consumers show the
      // in-flight state from `isFetching`.
      keepPreviousData: true,
      onError: (envelope) => {
        log.error("transactions fetch failed", {
          resource,
          status: envelope.statusCode,
          code: envelope.error.error_code,
          correlationId: envelope.error.correlation_id
        });
        if (notifyOnError) notifyError(envelope);
      }
    }
  );
  const payload = result.data;
  const rows = Array.isArray(payload) ? payload : Array.isArray(payload?.items) ? payload.items : [];
  const pageInfo = (Array.isArray(payload) ? void 0 : payload?.pagination) ?? result.envelope?.pagination;
  const stats = (Array.isArray(payload) ? void 0 : payload?.metadata?.stats) ?? {};
  const nextCursor = pageInfo?.next ?? null;
  const hasNext = Boolean(nextCursor);
  const hasPrev = cursorStack.length > 0;
  const goNext = React10.useCallback(() => {
    if (nextCursor) setCursorStack((stack) => [...stack, nextCursor]);
  }, [nextCursor]);
  const goPrev = React10.useCallback(() => {
    setCursorStack((stack) => stack.slice(0, -1));
  }, []);
  const reset = React10.useCallback(() => setCursorStack([]), []);
  const client = useApiClient();
  const fetchAllRows = React10.useCallback(
    async function* walk(opts = {}) {
      const max = Math.max(1, opts.maxRows ?? EXPORT_ROW_CEILING);
      const limit = Math.max(1, opts.pageSize ?? pageSize);
      const filters = JSON.parse(serializedQuery);
      let next;
      let seen = 0;
      for (; ; ) {
        const response = await client.get(`/${resource}`, {
          query: { ...filters, limit, cursor: next },
          ...opts.signal ? { signal: opts.signal } : {}
        });
        if (!response.ok) {
          throw new Error(
            `couldn\u2019t read every page for this export (${response.error.message})`
          );
        }
        const body = response.data;
        const page = Array.isArray(body) ? body : Array.isArray(body?.items) ? body.items : [];
        for (const row of page) {
          if (seen >= max) {
            opts.onTruncated?.(seen);
            return;
          }
          seen += 1;
          yield row;
        }
        const info = (Array.isArray(body) ? void 0 : body?.pagination) ?? response.pagination;
        next = info?.next ?? void 0;
        if (!next || page.length === 0) return;
      }
    },
    [client, resource, pageSize, serializedQuery]
  );
  return {
    rows,
    total: pageInfo?.total,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error,
    hasNext,
    hasPrev,
    goNext,
    goPrev,
    reset,
    refetch: result.refetch,
    pagination: { hasNext, hasPrev, onNext: goNext, onPrev: goPrev },
    stats,
    fetchAllRows
  };
}
var log2 = createLogger("tx-table:detail");
var POLL_INTERVAL_MS = 2500;
var MAX_POLL_ATTEMPTS = 48;
function useTransactionDetail(canonicalTxId, options = {}) {
  const { resource = "transactions", includeBody = false, enabled = true, notifyOnError = false } = options;
  const [activeScope, setActiveScope] = React10.useState(null);
  const [pollTarget, setPollTarget] = React10.useState(null);
  const [enrichmentTimedOut, setEnrichmentTimedOut] = React10.useState(false);
  const [retryNonce, setRetryNonce] = React10.useState(0);
  const attemptsRef = React10.useRef(0);
  const overridesKey = JSON.stringify(activeScope?.overrides ?? null);
  const subjectChanged = useDepsChanged([canonicalTxId]);
  const scopeChanged = useDepsChanged([overridesKey]);
  if (subjectChanged) setActiveScope(null);
  if (subjectChanged || scopeChanged) {
    setPollTarget(null);
    setEnrichmentTimedOut(false);
    attemptsRef.current = 0;
  }
  const result = useQuery(
    (client, signal) => {
      const readId = pollTarget ?? canonicalTxId ?? "";
      const query = scopeReadQuery(activeScope, {
        includeBody,
        enqueue: pollTarget ? false : void 0
      });
      return client.get(`/${resource}/${encodeURIComponent(readId)}`, {
        query,
        signal,
        // Opt out of the client's default 30s GET cache. This read backs the
        // surfaces that WRITE to the transaction — the inspector's attribution
        // chips, its notes and status — and each of them refetches on success to
        // show the result. A cached body turns that refetch into a no-op, so an
        // attached label reappears nowhere until the entry ages out, which reads
        // as "the write didn't take". The enrichment poll below has the same
        // problem in slower motion: successive polls share a key, so it would
        // poll the cache rather than the row.
        //
        // The entry can't simply be invalidated after a write either: the read
        // may be keyed by `chain:hash` while the writer only knows the canonical
        // id, and an in-flight GET that started pre-write will repopulate the
        // cache after any invalidation. Not caching a single-record read that is
        // already deduped by `useQuery` is the cheaper correctness.
        cacheTtlMs: 0
      });
    },
    {
      enabled: enabled && Boolean(canonicalTxId),
      deps: [canonicalTxId, overridesKey, includeBody, resource, pollTarget, retryNonce],
      onError: (envelope2) => {
        log2.error("transaction detail fetch failed", {
          resource,
          status: envelope2.statusCode,
          code: envelope2.error.error_code,
          correlationId: envelope2.error.correlation_id
        });
        if (notifyOnError) notifyError(envelope2);
      }
    }
  );
  const raw = result.data;
  const detail = React10.useMemo(() => {
    if (raw == null) return void 0;
    const parsed = TransactionDetailSchema.safeParse(raw);
    if (parsed.success) return parsed.data;
    log2.warn("transaction detail shape mismatch; using raw payload", {
      issues: parsed.error.issues.length
    });
    return raw;
  }, [raw]);
  const envelope = result.envelope;
  const debugResources = React10.useMemo(() => readDebugResources(envelope), [envelope]);
  const enrichmentStatus = detail?.enrichment_status ?? null;
  const isEnriching = enrichmentStatus === "processing";
  const enrichmentFailed = enrichmentStatus === "failed";
  React10.useEffect(() => {
    if (!isEnriching) return;
    const target = detail?.chain_id && detail?.tx_hash ? `${detail.chain_id}:${detail.tx_hash}` : canonicalTxId ?? null;
    setPollTarget((prev) => prev === target ? prev : target);
  }, [isEnriching, detail?.chain_id, detail?.tx_hash, canonicalTxId]);
  React10.useEffect(() => {
    if (!isEnriching || !pollTarget) return;
    if (result.isFetching) return;
    if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
      setEnrichmentTimedOut(true);
      return;
    }
    const timer = window.setTimeout(() => {
      attemptsRef.current += 1;
      result.refetch();
    }, POLL_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [isEnriching, pollTarget, result.isFetching, result.refetch]);
  const retryEnrichment = React10.useCallback(() => {
    attemptsRef.current = 0;
    setEnrichmentTimedOut(false);
    setPollTarget(null);
    setRetryNonce((n) => n + 1);
  }, []);
  return {
    detail,
    debugResources,
    otherScopes: detail?.other_scopes ?? [],
    activeScope,
    selectScope: setActiveScope,
    enrichmentStatus,
    isEnriching,
    enrichmentFailed,
    enrichmentTimedOut,
    retryEnrichment,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch
  };
}
var TRANSACTION_DEBUG_RESOURCE = "transaction";
function buildGroup(stats) {
  const fields = [
    {
      key: "decoder_version",
      label: "Decoder version",
      value: stats.decoderVersion ?? null,
      kind: "text",
      hint: "The decode_transaction build that produced this decode."
    },
    {
      key: "catalog_version",
      label: "Catalogue version",
      value: stats.catalogVersion ?? null,
      kind: "text",
      hint: "The authe-commons build whose protocol catalogue was in play. It moves independently of the decoder, and a change in WHICH protocol a transaction was attributed to is usually explained here, not above. Unset on bodies written before the catalogue build was stamped."
    },
    {
      key: "semantic_schema_version",
      label: "Semantic schema",
      value: stats.semanticSchemaVersion ?? null,
      kind: "text"
    },
    {
      key: "decoded_at",
      label: "Decoded at",
      value: stats.decodedAt ?? null,
      kind: "timestamp",
      hint: "When the decode ran \u2014 not when the transaction was mined."
    }
  ];
  if (!stats.decoderVersion && !stats.decodedAt && !stats.semanticSchemaVersion) {
    return {
      key: "decode_build",
      label: "Decode build",
      fields: [
        {
          key: "decode_meta",
          label: "Build",
          value: null,
          kind: "text",
          hint: "This body carries no decode_meta block \u2014 it predates the decode provenance record, so which build produced it is not recoverable."
        }
      ]
    };
  }
  return { key: "decode_build", label: "Decode build", fields };
}
function timingGroup(stats) {
  const fields = [
    {
      key: "decode_duration",
      label: "Decode duration",
      value: stats.durationMs ?? null,
      kind: "duration_ms"
    },
    ...Object.entries(stats.timings).map(
      ([phase, ms]) => ({
        key: `timing_${phase}`,
        // `call_ms` → "call" — the unit is already carried by the kind.
        label: phase.replace(/_ms$/, "").replace(/_/g, " "),
        value: ms,
        kind: "duration_ms"
      })
    )
  ];
  if (Object.keys(stats.timings).length === 0) {
    const profiledOff = stats.decoderVersion !== void 0;
    return {
      key: "decode_timings",
      label: "Decode timings",
      fields: [
        {
          key: "decode_timings_absent",
          label: "Timings",
          value: null,
          kind: "text",
          hint: profiledOff ? "This decode ran with emit_decode_meta off, so only the build identity above was recorded \u2014 the per-phase breakdown was not." : "This body carries no decode_meta \u2014 it predates the timing record."
        }
      ]
    };
  }
  return { key: "decode_timings", label: "Decode timings", fields };
}
function configGroup(cfg) {
  const phases = Object.entries(cfg.phases);
  const deps = Object.entries(cfg.abiDeps);
  if (phases.length === 0 && deps.length === 0 && cfg.missingAbiTiers.length === 0) {
    return null;
  }
  const fields = [
    // Lead with what could not run: it is the finding, where the rest is
    // context. Absent when every tier was wired, which is itself the answer.
    ...cfg.missingAbiTiers.length ? [
      {
        key: "missing_abi_tiers",
        label: "ABI tiers unavailable",
        value: cfg.missingAbiTiers,
        kind: "json",
        hint: "Resolution tiers that could not run, and the dependency each was missing."
      }
    ] : [],
    ...phases.map(([name, phase]) => ({
      key: `phase_${name}`,
      label: name.replace(/_/g, " "),
      value: phase.value ? "on" : "off",
      kind: "text",
      // "off by default" and "off because someone set it" are different
      // findings; the source is what separates them.
      hint: phase.source ? `from ${phase.source}` : void 0
    })),
    ...deps.map(([dep, state]) => ({
      key: `abi_dep_${dep}`,
      label: dep,
      value: state,
      kind: "text"
    }))
  ];
  return { key: "decode_config", label: "Decode config", fields };
}
function stepGroup(stats) {
  if (stats.steps.length === 0) {
    return {
      key: "pipeline_steps",
      label: "Pipeline steps",
      fields: [
        {
          key: "pipeline_steps_absent",
          label: "Steps",
          value: null,
          kind: "text",
          hint: "No enrichment layers on this body. Either the decoded slice was offloaded and not merged into the copy loaded here, or the body predates the per-step timeline."
        }
      ]
    };
  }
  return {
    key: "pipeline_steps",
    label: `Pipeline steps (${stats.steps.length})`,
    fields: stats.steps.map(
      (step) => ({
        key: `step_${step.name}`,
        label: step.name,
        value: [
          step.version ? `v${step.version}` : null,
          step.offsetMs === void 0 ? null : `+${Math.round(step.offsetMs)}ms`
        ].filter(Boolean).join(" \xB7 "),
        kind: "text",
        hint: [
          step.kinds.length ? step.kinds.join(", ") : null,
          step.confidence === void 0 ? null : `confidence ${step.confidence}`
        ].filter(Boolean).join(" \u2014 ")
      })
    )
  };
}
function bodyDebugGroups(body) {
  if (!body || typeof body !== "object") return [];
  const stats = parseProcessingStats(body);
  const config = parseDecodeConfig(body);
  return [buildGroup(stats), configGroup(config), timingGroup(stats), stepGroup(stats)].filter(
    (g) => g !== null
  );
}
function useTransactionDebug({
  canonicalTxId,
  debugResources = [],
  body,
  enabled = true
}) {
  const bodyGroups = React10.useMemo(() => bodyDebugGroups(body), [body]);
  const served = debugResources.find((r) => r.resource === TRANSACTION_DEBUG_RESOURCE);
  const groups = [...served?.groups ?? [], ...bodyGroups];
  const entry = enabled && groups.length > 0 ? {
    resource: TRANSACTION_DEBUG_RESOURCE,
    label: "Transaction",
    id: served?.id ?? canonicalTxId ?? null,
    groups
  } : null;
  useDebugResource(entry);
}
function toTokenFlow(tx, options) {
  return {
    flows: toTokenFlowRows(tx.token_flows ?? [], options),
    net: toNetFlowRows(tx.net_flows ?? [], options)
  };
}

// src/useGroupView.ts
var log3 = createLogger("tx-table:group");
function useGroupView(groupId, options = {}) {
  const { resource = "groups", includeScene = true, limit, enabled = true, notifyOnError = false } = options;
  const result = useQuery(
    (client, signal) => client.get(`/${resource}/${encodeURIComponent(groupId ?? "")}`, {
      query: {
        // Only send include_scene when turning it OFF — the backend defaults on.
        include_scene: includeScene ? void 0 : "false",
        limit: limit ?? void 0
      },
      signal
    }),
    {
      enabled: enabled && Boolean(groupId),
      deps: [groupId, includeScene, limit, resource],
      onError: (envelope) => {
        log3.error("group view fetch failed", {
          resource,
          status: envelope.statusCode,
          code: envelope.error.error_code,
          correlationId: envelope.error.correlation_id
        });
        if (notifyOnError) notifyError(envelope);
      }
    }
  );
  const payload = result.data;
  const group = React10.useMemo(() => {
    if (payload?.scene == null) return void 0;
    try {
      return parseGroupView(payload);
    } catch (err) {
      log3.warn("group view parse failed; rendering members only", { error: String(err) });
      return void 0;
    }
  }, [payload]);
  const transactions = React10.useMemo(
    () => coerceTransactionRows(payload?.transactions ?? []),
    [payload]
  );
  return {
    group,
    transactions,
    descriptor: payload?.group,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch
  };
}
function channelKey(channel) {
  return typeof channel === "string" ? channel : JSON.stringify(channel);
}
function matchesGroup(message, groupId) {
  const payload = message.payload;
  if (!payload || typeof payload !== "object") return false;
  if (String(payload.id ?? "") === groupId && groupId !== "") return true;
  const header = payload.header;
  if (header && typeof header === "object" && String(header.group_id ?? "") === groupId && groupId !== "") {
    return true;
  }
  return false;
}
function useGroupViewLive(groupId, options = {}) {
  const { channels, debounceMs = 400, ...viewOptions } = options;
  const base = useGroupView(groupId, viewOptions);
  const client = useOptionalStreamingClient();
  const [lastEventAt, setLastEventAt] = React10.useState(null);
  const [eventCount, setEventCount] = React10.useState(0);
  if (useDepsChanged([groupId])) {
    setLastEventAt(null);
    setEventCount(0);
  }
  const refetchRef = React10.useRef(base.refetch);
  refetchRef.current = base.refetch;
  const timer = React10.useRef(null);
  const list = React10.useMemo(
    () => channels == null ? [] : Array.isArray(channels) ? [...channels] : [channels],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      channels == null ? "" : Array.isArray(channels) ? channels.map(channelKey).join("|") : channelKey(channels)
    ]
  );
  const enabled = viewOptions.enabled ?? true;
  const isLive = Boolean(client) && list.length > 0 && enabled && Boolean(groupId);
  React10.useEffect(() => {
    if (!isLive || !client || !groupId) return;
    const onMessage = (message) => {
      if (!matchesGroup(message, groupId)) return;
      setLastEventAt(Date.now());
      setEventCount((n) => n + 1);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => refetchRef.current(), debounceMs);
    };
    const offs = list.map(
      (ch) => typeof ch === "string" ? client.subscribe(ch, onMessage) : client.subscribeChannel(ch, onMessage)
    );
    return () => {
      for (const off of offs) off();
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, [client, list, isLive, groupId, debounceMs]);
  return { ...base, isLive, lastEventAt, eventCount };
}
var OWN_DESCRIPTION = "The copy resolved for you (your private row, else the tenant-shared one).";
function buildScopeChoices(otherScopes, activeScope, ownLabel = "Your view") {
  return buildScopeChoices$1(otherScopes, activeScope, {
    ownLabel,
    ownDescription: OWN_DESCRIPTION,
    scopeLabel: transactionScopeLabel,
    scopeDescription: transactionScopeDescription
  });
}
function TransactionScopeSwitcher({
  otherScopes,
  activeScope = null,
  onSelect,
  busy = false,
  className
}) {
  return /* @__PURE__ */ jsx(
    ScopeSwitcher,
    {
      otherScopes,
      activeScope,
      onSelect,
      busy,
      className,
      copy: {
        ownLabel: "Your view",
        ownDescription: OWN_DESCRIPTION,
        scopeLabel: transactionScopeLabel,
        scopeDescription: transactionScopeDescription,
        ariaLabel: "View this transaction at another scope"
      }
    }
  );
}
function TransactionDetailPanel({
  canonicalTxId,
  children,
  onScopeChange,
  className,
  ...queryOptions
}) {
  const { detail, otherScopes, activeScope, selectScope, isLoading, isFetching, error, refetch } = useTransactionDetail(canonicalTxId, queryOptions);
  const handleSelect = React10.useCallback(
    (scope) => {
      selectScope(scope);
      onScopeChange?.(scope);
    },
    [selectScope, onScopeChange]
  );
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("flex items-center justify-center py-10 text-muted-foreground", className), children: /* @__PURE__ */ jsx(Spinner, {}) });
  }
  if (error) {
    return /* @__PURE__ */ jsx(ErrorState, { error, onRetry: refetch, className, size: "sm" });
  }
  if (!detail) return /* @__PURE__ */ jsx("div", { className: cn("py-10 text-center text-sm text-muted-foreground", className) });
  const viewingLabel = activeScope ? transactionScopeLabel(activeScope.scope) : "Your view";
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
      /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "Viewing" }),
      /* @__PURE__ */ jsx(Badge, { variant: activeScope ? "outline" : "secondary", children: viewingLabel }),
      detail.saved && !activeScope ? /* @__PURE__ */ jsx(Badge, { variant: "outline", title: "This copy is saved to your workspace.", children: "Saved" }) : null,
      isFetching ? /* @__PURE__ */ jsx(Spinner, { className: "h-3.5 w-3.5 text-muted-foreground" }) : null
    ] }),
    /* @__PURE__ */ jsx(
      TransactionScopeSwitcher,
      {
        otherScopes,
        activeScope,
        onSelect: handleSelect,
        busy: isFetching
      }
    ),
    children ? children(detail) : null
  ] });
}
var log4 = createLogger("tx-table:hydration");
var TX_LARGE_ATTRIBUTES = [
  "input",
  "output",
  "data",
  "logs",
  "internal_transactions",
  "native_transfers",
  "erc20_transfers",
  "nft_transfers",
  "decoded_transaction",
  "decoded_call",
  // The protocol-shaped semantic tree (`CanonicalTransaction.semantic`) the
  // `@authe/tx-semantic` view renders. Offloaded like the other heavy forensic
  // attributes and hydrated on drill-down.
  "semantic"
];
function createCdnOffloadFetcher(config) {
  return async (placeholder) => {
    const url = cdnUrlForKey(placeholder.key, config);
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "omit"
    });
    if (!res.ok) throw new Error(`CDN offload fetch failed (${res.status}) for ${placeholder.attribute}`);
    return res.json();
  };
}
function hasOffloadedAttributes(row, attributes = TX_LARGE_ATTRIBUTES) {
  return attributes.some((attr) => isOffloadPlaceholder(row[attr]));
}
async function hydrateTxRow(row, fetcher, attributes = TX_LARGE_ATTRIBUTES) {
  if (!hasOffloadedAttributes(row, attributes)) return row;
  const attrSet = new Set(attributes);
  return rehydrateItem(row, (placeholder) => {
    if (!attrSet.has(placeholder.attribute)) return Promise.resolve(row[placeholder.attribute]);
    return fetcher(placeholder);
  });
}
function useHydratedTxRow(row, options) {
  const { fetcher, attributes = TX_LARGE_ATTRIBUTES, enabled = true } = options;
  const [hydrated, setHydrated] = React10.useState(row);
  const [isLoading, setIsLoading] = React10.useState(false);
  const [error, setError] = React10.useState(null);
  const [trigger, setTrigger] = React10.useState(0);
  if (useDepsChanged([row])) {
    setHydrated(row);
    setError(null);
  }
  React10.useEffect(() => {
    if (!enabled && trigger === 0) return;
    if (!hasOffloadedAttributes(row, attributes)) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    hydrateTxRow(row, fetcher, attributes).then((next) => {
      if (!cancelled) setHydrated(next);
    }).catch((err) => {
      if (cancelled) return;
      log4.warn("row hydration failed", { error: String(err) });
      setError(err);
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [row, fetcher, enabled, trigger]);
  const hydrate = React10.useCallback(() => setTrigger((n) => n + 1), []);
  return { row: hydrated, isLoading, error, hydrate };
}
function useLazyAttribute(value, fetcher) {
  const placeholder = isOffloadPlaceholder(value) ? value : null;
  const [resolved, setResolved] = React10.useState(placeholder ? void 0 : value);
  const [isLoading, setIsLoading] = React10.useState(false);
  const [error, setError] = React10.useState(null);
  const runRef = React10.useRef(0);
  React10.useEffect(() => {
    runRef.current += 1;
    setResolved(isOffloadPlaceholder(value) ? void 0 : value);
    setIsLoading(false);
    setError(null);
  }, [value]);
  const load = React10.useCallback(() => {
    if (!placeholder || resolved !== void 0 || isLoading) return;
    const runId = ++runRef.current;
    const isCurrent = () => runId === runRef.current;
    setIsLoading(true);
    setError(null);
    fetcher(placeholder).then((v) => {
      if (isCurrent()) setResolved(v);
    }).catch((err) => {
      if (!isCurrent()) return;
      log4.warn("attribute hydration failed", { attribute: placeholder.attribute, error: String(err) });
      setError(err);
    }).finally(() => {
      if (isCurrent()) setIsLoading(false);
    });
  }, [placeholder, resolved, isLoading, fetcher]);
  return { value: resolved, isLoading, error, load };
}
function createCachedCdnOffloadFetcher(config, cache) {
  const base = createCdnOffloadFetcher(config);
  return async (placeholder) => {
    const cached = await cache.getStatic(placeholder.key);
    if (cached !== void 0) return cached;
    const value = await base(placeholder);
    await cache.setStatic(placeholder.key, value);
    return value;
  };
}
function txRowEnvelopeHeader(row) {
  return {
    canonical_tx_id: row.canonical_tx_id,
    chain_id: row.chain_id,
    pk: null,
    sk: null,
    block_hash: row.block_hash ?? "",
    block_number: row.block_number ?? 0,
    block_timestamp: row.block_timestamp ?? 0,
    finality: typeof row.finality === "string" ? row.finality : "unknown",
    participant_summary: row.participant_summary ?? {},
    total_value_raw: row.total_value_raw ?? "0",
    counts: row.counts ?? {},
    s3_key: row.s3_key ?? null,
    s3_etag: row.s3_etag ?? null,
    body_bytes: row.body_bytes ?? 0,
    body_compression: row.body_compression ?? "none",
    // The lean list projection carries the manifest (catchall passthrough) so the
    // body loader knows which heavy slices to lazy-load + merge after the core.
    body_slices: normalizeBodySlices(row.body_slices),
    schema_version: row.schema_version ?? "1"
  };
}
function normalizeBodySlices(value) {
  if (!value || typeof value !== "object") return void 0;
  const out = {};
  for (const [slice, ref] of Object.entries(value)) {
    const o = ref && typeof ref === "object" ? ref : {};
    out[slice] = {
      bytes: typeof o.bytes === "number" ? o.bytes : 0,
      etag: typeof o.etag === "string" ? o.etag : null
    };
  }
  return Object.keys(out).length > 0 ? out : void 0;
}
function createTxBodyLoader(config) {
  return createEnvelopeBodyLoader(config);
}
function useEnvelopeBody(row, loader, options = {}) {
  const { enabled = false, mergeSlices = false } = options;
  const [body, setBody] = React10.useState(void 0);
  const [isLoading, setIsLoading] = React10.useState(false);
  const [error, setError] = React10.useState(null);
  const [pendingSlices, setPendingSlices] = React10.useState([]);
  const [failedSlices, setFailedSlices] = React10.useState([]);
  const [trigger, setTrigger] = React10.useState(enabled ? 1 : 0);
  const sliceSelector = Array.isArray(mergeSlices) ? [...mergeSlices].sort().join(",") : String(mergeSlices);
  if (useDepsChanged([row.canonical_tx_id, row.s3_etag, enabled])) {
    setBody(void 0);
    setError(null);
    setPendingSlices([]);
    setFailedSlices([]);
    setTrigger(enabled ? 1 : 0);
  }
  React10.useEffect(() => {
    if (trigger === 0) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setFailedSlices([]);
    const header = txRowEnvelopeHeader(row);
    const wantedSlices = () => {
      const manifest = header.body_slices ?? {};
      const names2 = Array.isArray(mergeSlices) ? mergeSlices : mergeSlices ? Object.keys(manifest) : [];
      return names2.filter((slice) => manifest[slice]);
    };
    void (async () => {
      try {
        const core = await loader.loadBody(header).get();
        if (cancelled) return;
        setBody(core);
        const slices = wantedSlices();
        setPendingSlices(slices);
        if (slices.length === 0) return;
        const working = cloneBody(core);
        await Promise.all(
          slices.map(async (slice) => {
            try {
              const payload = await loader.loadSlice(header, slice);
              if (cancelled || payload === void 0) return;
              mergeSlice(working, slice, payload);
              setBody({ ...working });
            } catch (err) {
              if (!cancelled) {
                log4.warn("envelope slice load failed", {
                  tx: row.canonical_tx_id,
                  slice,
                  error: String(err)
                });
                setFailedSlices((prev) => prev.includes(slice) ? prev : [...prev, slice]);
              }
            } finally {
              if (!cancelled) {
                setPendingSlices((prev) => prev.filter((s) => s !== slice));
              }
            }
          })
        );
      } catch (err) {
        if (cancelled) return;
        log4.warn("envelope body load failed", { tx: row.canonical_tx_id, error: String(err) });
        setError(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [row.canonical_tx_id, row.s3_etag, loader, trigger, sliceSelector]);
  const load = React10.useCallback(() => setTrigger((n) => n === 0 ? 1 : n + 1), []);
  return { body, isLoading, error, pendingSlices, failedSlices, load };
}

// src/graphqlFeed.ts
function feedRowToEnrichedTxRow(row) {
  const primaryEntity = row.entities[0];
  const base = coerceTransactionRow({
    canonical_tx_id: row.canonicalTxId,
    chain_id: row.chainId,
    block_timestamp: row.blockTimestamp,
    primary_entity_id: primaryEntity?.id ?? null
  });
  const glyph = (ref) => ({
    logo: ref.logo,
    color: ref.color
  });
  const labels = row.labels.map((l) => ({ label: l.name ?? l.catalogId, ...glyph(l) }));
  const tags = row.tags.map((t) => ({ label: t.name ?? t.catalogId, ...glyph(t) }));
  const groups = row.groups.map((g) => ({ id: g.catalogId, name: g.name, ...glyph(g) }));
  const enrichment = {
    labels,
    tags,
    groups,
    entity: primaryEntity ? { id: primaryEntity.id, name: primaryEntity.name ?? primaryEntity.id } : null
  };
  return { ...base, enrichment };
}
function feedToEnrichedRows(feed) {
  if (!feed) return [];
  const items = Array.isArray(feed) ? feed : feed.items;
  return (items ?? []).map(feedRowToEnrichedTxRow);
}
function enrichFallbackRow(raw) {
  const row = coerceTransactionRow(raw);
  const r = row;
  const to = asString(r.to);
  const entityName = asString(r.to_entity_name) ?? asString(r.to_address_label);
  const enrichment = {
    from: asString(r.from),
    to,
    entity: entityName ? { id: to ?? entityName, name: entityName } : null
  };
  return { ...row, enrichment };
}
var EMPTY_ROWS = [];
function restItems(payload) {
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload?.items) ? payload.items : [];
}
function useLatestTransactionsFeed(options = {}) {
  const limit = options.limit ?? 25;
  const active = useTransport("transactions.recentFeed") === "graphql";
  const query = useGraphqlQuery(
    "transactions.recentFeed",
    {
      graphql: async (client, signal) => feedToEnrichedRows(await fetchLatestTransactions(client, { limit }, { signal })),
      rest: async (client, signal) => {
        const payload = unwrapData(
          await client.get("/transactions", { query: { limit }, signal })
        );
        return restItems(payload).map(enrichFallbackRow);
      }
    },
    { enabled: (options.enabled ?? true) && active, deps: [limit] }
  );
  const rows = query.data ?? EMPTY_ROWS;
  return {
    rows,
    isLoading: query.isLoading && active,
    isFetching: query.isFetching,
    error: query.error,
    active,
    refetch: query.refetch
  };
}

export { CLIENT_CONTAINS_FIELDS, CLIENT_FACET_FIELDS, EXPORT_ROW_CEILING, TRANSACTIONS_TABLE_ID, TRANSACTION_DEBUG_RESOURCE, TX_COLUMN_LABELS, TX_EXPORT_COLUMNS, TX_FILTER_CODEC, TX_FILTER_OPS, TX_LARGE_ATTRIBUTES, TransactionDetailPanel, TransactionExportMenu, TransactionFacets, TransactionLegend, TransactionScopeSwitcher, TransactionTable, bodyDebugGroups, buildScopeChoices, classifyTxRows, columnFiltersToFilterRules, createCachedCdnOffloadFetcher, createCdnOffloadFetcher, createTransactionColumns, createTxBodyLoader, feedRowToEnrichedTxRow, feedToEnrichedRows, filterRulesToColumnFilters, filterRulesToQuery, flaggedIds, getRowEnrichment, hasOffloadedAttributes, hydrateTxRow, isSystemTxRow, matchesGroup, mergeTransactionRows, otherParty, resolveRisk, rowProtocols, summarizeTransactions, toTokenFlow, txRowEnvelopeHeader, txRowId, txRowKey, txSelectionRowId, txStreamKey, useChainHeads, useEnvelopeBody, useGroupView, useGroupViewLive, useHydratedTxRow, useLatestTransactionsFeed, useLazyAttribute, useLiveFinalityRows, useTransactionDebug, useTransactionDetail, useTransactionStream, useTransactionViews, useTransactionsQuery, withLiveFinality, withoutDustRows };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map